import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import './RangeSliderEditor.less';
import { IEditorProps } from './Editor';
import { RangeSlider } from 'components/inputs/RangeSlider';
import { EditorLabel } from './EditorLabel';
import { useEditorId } from './hooks/useEditorId';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { useTabbingContext } from 'contexts/TabbingContext';
import debounce from 'debounce';
import { initializeNumericValue, parseInputToNumber } from 'utils/NumericFormattingUtils';
import { useDebounceStringProperty } from 'hooks/useDebounceStringProperty';
import { childValueIsNull } from 'components/property-editors/utils/ProviderNodeUtils';

interface IRangeSliderEditorProps extends IEditorProps {}

/**
 *
 *
 * Copyright (c) 2022 Decisions All Rights Reserved
 */
export function RangeSliderEditor({ child, path, onChange }: IRangeSliderEditorProps) {
  const [value, setValue] = useState(child.Value.OutputValue ?? 0);
  const sliderRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const min = getEditorConfigValue<number>(child, 'MinValue');
  const max = getEditorConfigValue<number>(child, 'MaxValue');
  const step = getEditorConfigValue<number>(child, 'StepValue');

  const changeValue = useMemo(() => debounce((value: number) => onChange(value, path), 200), [onChange, path]);

  const onValueChange = useCallback(
    (value: string) => {
      let newValue = +value;
      if (newValue < min) {
        newValue = min;
      } else if (newValue > max) {
        newValue = max;
      }
      setValue(newValue);
      changeValue(newValue);
    },
    [changeValue, max, min]
  );

  useEffect(() => {
    if (sliderRef.current === document.activeElement || inputRef.current === document.activeElement) {
      return;
    }

    setValue(child.Value.OutputValue ?? 0);
  }, [child.Value.OutputValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!sliderRef.current) {
        return;
      }

      if (e.key === 'ArrowUp') {
        // avoid 0.3-0.1=0.19999999999999998
        sliderRef.current.stepUp();
        onValueChange(sliderRef.current.value);
      } else if (e.key === 'ArrowDown') {
        // avoid 0.3-0.1=0.19999999999999998
        sliderRef.current.stepDown();
        onValueChange(sliderRef.current.value);
      }
    },
    [onValueChange]
  );

  const formatHelper = useCallback((value: string) => parseInputToNumber(value, true), []);

  const initialValueHelper = (value: string | null) => (!childValueIsNull(child) ? initializeNumericValue(value) : '');

  const { fieldValue, handleChange } = useDebounceStringProperty({
    value: value + '',
    path,
    formatHelper,
    initialValueHelper,
    onChange: (value, _) => onValueChange(value),
    skipValueUpdate: false,
  });

  const [id] = useEditorId(path);
  const tabIndex = useTabbingContext();
  return (
    <EditorLabel editor={child} path={path}>
      <div className="dp-range-slider-editor">
        <RangeSlider
          id={id + '-slider'}
          value={value}
          max={max}
          min={min}
          step={step}
          ref={sliderRef}
          className="dpc-time-span-editor__range"
          onChange={e => onValueChange(parseInputToNumber(e.target.value, true)!)}
        />
        <input
          id={id + '-input'}
          value={fieldValue} // re-cast to string to avoid leading 0s
          className="dp-range-slider-editor__input"
          type="text"
          onChange={handleChange}
          tabIndex={tabIndex}
          onKeyDown={handleKeyDown}
          ref={inputRef}
        />
      </div>
    </EditorLabel>
  );
}
